import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "../../assets/logo.svg";
import { useSidebar } from "../../context/SidebarContext";
import { usePermission } from "../../context/PermissionContext";
import {
  FiMenu,
  FiX,
  FiGrid,
  FiUsers,
  FiVideo,
  FiSettings,
  FiChevronDown,
  FiGift,
  FiShield,
  FiPlayCircle,
  FiCheckSquare,
  FiLogOut,
} from "react-icons/fi";

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const OPEN_GROUPS_KEY = "sidebar:openGroups";

// Each group carries its own icon so the section is instantly
// recognizable even before reading the title text.
const navGroups = [
  {
    title: "Overview",
    icon: FiGrid,
    items: [
      { to: "/dashboard", label: "Dashboard", module: "dashboard" },
      { to: "/announcement", label: "Announcement", module: "announcement" },
      { to: "/weekly-leaderboard", label: "Weekly Leaderboard", module: "weekly-leaderboard" },
    ],
  },
  {
    title: "Users & Access",
    icon: FiUsers,
    items: [
      { to: "/user", label: "Users", module: "users" },
      { to: "/pioneer-list", label: "Pioneer List", module: "pioneer" },
      { to: "/duplicate-account", label: "Duplicate Account", module: "duplicate-account" },
    ],
  },
  {
    title: "Event Hub",
    icon: FiGift,
    items: [
      { to: "/event", label: "Events List", aliases: ["/events", "/event/"], module: "event" },
      { to: "/reward-coin-approval", label: "Reward Coin Approval", module: "reward-coin-approval" },
      { to: "/exchange-orders", label: "Exchange Orders", module: "exchange" },
      { to: "/exchange-currencies", label: "Exchange Currencies", module: "exchange" },
    ],
  },
  {
    title: "Creator Hub",
    icon: FiVideo,
    items: [
      { to: "/profile-verifications", label: "Creator Profile", module: "profile-verification" },
      { to: "/creator-hub", label: "Content Request List", module: "creator-hub" },
      { to: "/milestone", label: "Bonus Claim", module: "milestone" },
    ],
  },
  {
    title: "Games",
    icon: FiPlayCircle,
    items: [
      { to: "/games", label: "Game Management", module: "games" },
      { to: "/boom-cards-game", label: "Boom Cards Game", module: "boom-cards-game" },
      { to: "/tap-game", label: "Tap Game", module: "tap-game" },
      { to: "/game-2048", label: "2048", module: "game-2048" },
      { to: "/fruit-cut", label: "Fruit Cut", module: "fruit-cut" },
      { to: "/slots-game", label: "Slot Machine", module: "slots-game" },
      { to: "/spin-wheel-game", label: "Spin Wheel", module: "spin-wheel-game" },
    ],
  },
  {
    title: "Tasks",
    icon: FiCheckSquare,
    items: [{ to: "/tasks", label: "Task Management", module: "task" }],
  },
  {
    title: "Access Control",
    icon: FiShield,
    items: [
      { to: "/staff", label: "Staff", module: "staff" },
      { to: "/roles", label: "Roles", module: "roles" },
    ],
  },
  {
    title: "System",
    icon: FiSettings,
    items: [
      { to: "/developer-collaboration", label: "Collaboration", module: "devloper-collaboration" },
      { to: "/sponsor-applications", label: "Sponsor Applications", module: "sponsor-application" },
      { to: "/notifications", label: "Notification", module: "notification" },
      { to: "/kyc-settings", label: "KYC Settings", module: "kyc-settings" },
      { to: "/sms-verification", label: "SMS Verification", module: "sms-verification" },
      { to: "/settings", label: "Settings", module: "setting" },
    ],
  },
];

// A route belongs to an item when it matches exactly, sits below it, or
// matches one of the item's declared aliases.
const matchesItem = (item, pathname) =>
  pathname === item.to ||
  pathname.startsWith(`${item.to}/`) ||
  (item.aliases || []).some((alias) => pathname.startsWith(alias));

const readStoredGroups = () => {
  try {
    const raw = localStorage.getItem(OPEN_GROUPS_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

const SidebarItem = ({ item, close }) => {
  const location = useLocation();
  const isStateMatch = location.state && location.state.from === item.to;
  const active = isStateMatch || (!location.state?.from && matchesItem(item, location.pathname));

  return (
    <li>
      <Link
        to={item.to}
        onClick={close}
        className={`group relative flex items-center pl-5 pr-3 py-2 rounded-lg text-[13px] transition-all duration-200
          ${
            active
              ? "bg-lime-300/10 text-lime-300 font-medium"
              : "text-gray-400 hover:bg-gray-800/40 hover:text-gray-200"
          }
        `}
      >
        {/* marker rail tying the item back to its group */}
        <span
          className={`absolute left-0 top-1 bottom-1 w-[2px] rounded-full transition-colors duration-200 ${
            active ? "bg-lime-300" : "bg-transparent group-hover:bg-gray-700"
          }`}
        />
        <span className="transform transition-transform duration-200 group-hover:translate-x-0.5">
          {item.label}
        </span>
      </Link>
    </li>
  );
};

const SidebarGroup = ({ group, expanded, hasActiveChild, onToggle, close }) => {
  const GroupIcon = group.icon;

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-all duration-200 border
          ${
            hasActiveChild
              ? "border-lime-300/60 bg-lime-300/5 text-lime-300 font-semibold"
              : "border-transparent text-gray-300 hover:bg-gray-800/40 hover:text-white"
          }
        `}
      >
        <GroupIcon size={16} className={hasActiveChild ? "text-lime-300" : "text-gray-500"} />
        <span className="flex-1 text-left">{group.title}</span>
        <FiChevronDown
          size={15}
          className={`transition-transform duration-200 ${expanded ? "rotate-180" : ""} ${
            hasActiveChild ? "text-lime-300" : "text-gray-500"
          }`}
        />
      </button>

      {/* Items collapse via a grid-rows transition so the panel never jumps */}
      <div
        className={`grid transition-all duration-200 ease-in-out ${
          expanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <ul className="overflow-hidden ml-[22px] mt-1 space-y-0.5 border-l border-gray-800/60 pl-2">
          {group.items.map((item) => (
            <SidebarItem key={item.to} item={item} close={close} />
          ))}
        </ul>
      </div>
    </li>
  );
};

const Sidebar = () => {
  const { isOpen, toggle, close } = useSidebar();
  const { hasPermission, reload: reloadPermissions } = usePermission();
  const navigate = useNavigate();
  const location = useLocation();

  // Module-gated: an item with no `module` (none currently) always shows;
  // an item whose module the admin can't view is dropped, and a group left
  // with nothing in it is dropped too.
  const visibleGroups = useMemo(
    () =>
      navGroups
        .map((group) => ({
          ...group,
          items: group.items.filter((item) => hasPermission(item.module, "view")),
        }))
        .filter((group) => group.items.length > 0),
    [hasPermission]
  );

  // The group owning the current route, so it can open itself on load.
  const activeTitle = useMemo(() => {
    const match = visibleGroups.find((group) =>
      group.items.some((item) => matchesItem(item, location.pathname))
    );
    return match ? match.title : null;
  }, [visibleGroups, location.pathname]);

  const [openGroups, setOpenGroups] = useState(() => readStoredGroups() || []);

  // Keep the active route's group open without collapsing what the admin
  // already expanded themselves.
  useEffect(() => {
    if (!activeTitle) return;
    setOpenGroups((prev) => (prev.includes(activeTitle) ? prev : [...prev, activeTitle]));
  }, [activeTitle]);

  useEffect(() => {
    try {
      localStorage.setItem(OPEN_GROUPS_KEY, JSON.stringify(openGroups));
    } catch {
      // storage unavailable (private mode / blocked) - state stays in memory
    }
  }, [openGroups]);

  const toggleGroup = useCallback((title) => {
    setOpenGroups((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  }, []);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem("token");
      if (token) {
        await fetch(`${baseUrl}/auth/logout`, {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {
      // proceed with logout regardless
    } finally {
      localStorage.removeItem("token");
      await reloadPermissions();
      navigate("/");
    }
  };

  return (
    <>
      {/* Mobile hamburger toggle */}
      {!isOpen && (
        <button
          onClick={toggle}
          className="fixed top-4 left-4 z-40 lg:hidden bg-gray-900 text-white p-2 rounded-lg shadow-lg border border-gray-800/60"
          aria-label="Open sidebar"
        >
          <FiMenu size={20} />
        </button>
      )}

      {/* Mobile overlay backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-20 lg:hidden transition-opacity duration-300"
          onClick={close}
        />
      )}

      {/* Sidebar panel */}
      <div
        className={`
          fixed top-0 left-0 h-screen w-[260px] bg-gray-900 text-gray-300 flex flex-col z-30 transition-transform duration-300 shadow-2xl border-r border-gray-800/50
          lg:sticky lg:top-0 lg:h-screen lg:translate-x-0 lg:z-auto lg:flex-shrink-0
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
      >
        {/* Logo + close */}
        <div className="flex items-center justify-between px-6 pt-6 pb-5">
          <img src={logo} className="h-8 drop-shadow-md" alt="Admin Panel" />
          <button
            onClick={close}
            className="text-gray-500 hover:text-white transition-colors lg:hidden p-1.5 bg-gray-800/40 rounded-lg"
            aria-label="Close sidebar"
          >
            <FiX size={18} />
          </button>
        </div>

        {/* Nav links */}
        <ul className="flex-1 overflow-y-auto scrollbar-dark px-3 py-2 space-y-1">
          {visibleGroups.map((group) => (
            <SidebarGroup
              key={group.title}
              group={group}
              expanded={openGroups.includes(group.title)}
              hasActiveChild={activeTitle === group.title}
              onToggle={() => toggleGroup(group.title)}
              close={close}
            />
          ))}
        </ul>

        {/* Logout at bottom */}
        <div className="p-4 border-t border-gray-800/50 bg-[#0f111a]/30">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm text-red-400/90 hover:bg-red-500/10 hover:text-red-400 transition-colors font-medium"
          >
            <FiLogOut size={15} />
            Logout
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
